import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  LinearProgress,
  IconButton,
  Alert,
  CircularProgress,
  Select,
  Option,
} from "@mui/joy";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../common/ResponsiveTable";
import {
  CheckCircle,
  XCircle as ErrorOutline,
  ClockClockwise as PendingActions,
  Sparkle as AutoAwesome,
  ArrowsClockwise as Refresh,
  Clock as Schedule,
  TrendUp as TrendingUp,
  Article,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";

interface GenerationProgress {
  id: number;
  title: string;
  status: string;
  progress: number;
  started_at: string;
  completed_at?: string;
  error_message?: string;
  task_id?: string;
  blog_idea: {
    title: string;
    primary_keyword: string;
  };
}

interface GenerationStats {
  total_generated: number;
  successful: number;
  failed: number;
  in_progress: number;
  average_time: number;
  total_cost: number;
}

export const ContentProgressTracker: React.FC = () => {
  const [timeFilter, setTimeFilter] = useState("today");

  // Fetch generation progress
  const {
    data: progressData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["generation-progress", timeFilter],
    queryFn: async () => {
      const response = await api.get(
        `/api/marketing/articles/generation-progress/?time_filter=${timeFilter}`,
      );
      return response.data;
    },
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  interface GenerationStats {
    total_generated: number;
    successful: number;
    in_progress: GenerationProgress[];
    average_time: number;
    total_cost: number;
    history: GenerationProgress[];
    errors: GenerationProgress[];
  }

  // Fetch generation stats
  const { data: stats } = useQuery<GenerationStats>({
    queryKey: ["generation-stats", timeFilter],
    queryFn: async () => {
      const response = await api.get<GenerationStats>(
        `/api/marketing/articles/generation-stats/?time_filter=${timeFilter}`,
      );
      return response.data;
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "content_done":
        return <CheckCircle />;
      case "error":
        return <ErrorOutline />;
      default:
        return <PendingActions />;
    }
  };

  const getStatusColor = (status: string): any => {
    switch (status) {
      case "content_done":
        return "success";
      case "error":
        return "danger";
      default:
        return "warning";
    }
  };

  const getStatusLabel = (status: string, progress: number) => {
    const labels: Record<string, string> = {
      pending: "Starting...",
      seo_done: "SEO Research",
      outline_done: "Creating Outline",
      brief_done: "Writing Brief",
      content_done: "Complete",
      error: "Failed",
    };
    return labels[status] || status;
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime).getTime();
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    const duration = Math.floor((end - start) / 1000);

    if (duration < 60) return `${duration}s`;
    if (duration < 3600)
      return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const historyColumns: ResponsiveColumn<GenerationProgress>[] = [
    {
      key: "title",
      sortValue: (row) => row.title,
      header: "Article Title",
      mobileVariant: "header",
      render: (item) => <Typography level="title-sm">{item.title}</Typography>,
    },
    {
      key: "blog_idea",
      sortValue: (row) => row.blog_idea?.title,
      header: "Blog Idea",
      render: (item) => (
        <Typography level="body-sm">{item.blog_idea?.title || "-"}</Typography>
      ),
    },
    {
      key: "keyword",
      sortValue: (row) => row.blog_idea?.primary_keyword,
      header: "Keyword",
      render: (item) => (
        <Chip size="sm" variant="soft">
          {item.blog_idea?.primary_keyword || "-"}
        </Chip>
      ),
    },
    {
      key: "status",
      sortValue: (row) => row.status,
      header: "Status",
      render: (item) => (
        <Chip
          variant="soft"
          color={getStatusColor(item.status)}
          size="sm"
          startDecorator={getStatusIcon(item.status)}
        >
          {getStatusLabel(item.status, item.progress)}
        </Chip>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (item) => (
        <Typography level="body-sm">
          {item.completed_at
            ? formatDuration(item.started_at, item.completed_at)
            : "-"}
        </Typography>
      ),
    },
    {
      key: "started",
      sortValue: (row) => (row.started_at ? new Date(row.started_at) : null),
      header: "Started",
      render: (item) => (
        <Typography level="body-sm">
          {new Date(item.started_at).toLocaleString()}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography
            level="h3"
            sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" } }}
          >
            Content Generation Progress
          </Typography>
          <Stack direction="row" spacing={1}>
            <Select
              value={timeFilter}
              onChange={(_, value) => setTimeFilter(value as string)}
              sx={{ flex: { xs: 1, sm: "none" } }}
            >
              <Option value="today">Today</Option>
              <Option value="week">This Week</Option>
              <Option value="month">This Month</Option>
              <Option value="all">All Time</Option>
            </Select>
            <IconButton onClick={() => refetch()}>
              <Refresh />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(5, 1fr)",
          },
          gap: { xs: 1, md: 2 },
          mb: 3,
        }}
      >
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Article color="var(--joy-palette-primary-500)" />
              <Box>
                <Typography level="h3">
                  {stats?.total_generated || 0}
                </Typography>
                <Typography level="body-sm">Total Generated</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircle color="var(--joy-palette-success-500)" />
              <Box>
                <Typography level="h3">{stats?.successful || 0}</Typography>
                <Typography level="body-sm">Successful</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PendingActions color="var(--joy-palette-warning-500)" />
              <Box>
                <Typography level="h3">
                  {stats?.in_progress?.length || 0}
                </Typography>
                <Typography level="body-sm">In Progress</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Schedule color="var(--joy-palette-primary-500)" />
              <Box>
                <Typography level="h3">
                  {stats?.average_time
                    ? `${Math.round(stats.average_time / 60)}m`
                    : "-"}
                </Typography>
                <Typography level="body-sm">Avg. Time</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrendingUp color="var(--joy-palette-primary-500)" />
              <Box>
                <Typography level="h3">
                  {stats?.total_cost == null
                    ? "Not tracked"
                    : `$${stats.total_cost.toFixed(2)}`}
                </Typography>
                <Typography level="body-sm">Total Cost</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Active Generations */}
      {(progressData as GenerationStats)?.in_progress?.length > 0 && (
        <Card variant="soft" sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              Active Generations
            </Typography>
            <Stack spacing={2}>
              {(progressData as GenerationStats).in_progress.map(
                (item: GenerationProgress) => (
                  <Box key={item.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography level="title-sm">{item.title}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          variant="soft"
                          color="warning"
                          size="sm"
                          startDecorator={<AutoAwesome />}
                        >
                          {getStatusLabel(item.status, item.progress)}
                        </Chip>
                        <CircularProgress size="sm" />
                      </Stack>
                    </Stack>
                    <LinearProgress
                      determinate
                      value={item.progress}
                      sx={{ height: 6 }}
                    />
                  </Box>
                ),
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Generation History */}
      <Card variant="outlined">
        <CardContent>
          <Typography level="h4" sx={{ mb: 2 }}>
            Generation History
          </Typography>

          {(progressData as GenerationStats)?.history?.length === 0 ? (
            <Alert color="neutral">
              <Typography>
                No content generation history for the selected time period.
              </Typography>
            </Alert>
          ) : (
            <ResponsiveTable<GenerationProgress>
              rows={(progressData as GenerationStats)?.history || []}
              getRowKey={(item) => item.id}
              columns={historyColumns}
            />
          )}
        </CardContent>
      </Card>

      {/* Error Summary */}
      {(progressData as GenerationStats)?.errors?.length > 0 && (
        <Card variant="soft" color="danger" sx={{ mt: 3 }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              Recent Errors
            </Typography>
            <Stack spacing={1}>
              {(progressData as GenerationStats).errors.map(
                (item: GenerationProgress) => (
                  <Alert key={item.id} color="danger">
                    <Stack>
                      <Typography level="title-sm">{item.title}</Typography>
                      <Typography level="body-sm">
                        {item.error_message}
                      </Typography>
                    </Stack>
                  </Alert>
                ),
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
